"use client";

import { useState } from "react";
import type { TimingWeek } from "@/lib/conditions/timing-data";
import { timingScoreStyle, TIMING_BANDS } from "@/lib/conditions/timing-display";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";

type Props = {
  weeks: TimingWeek[];
  seasonLabel: string;
  alternateSeasonLabel?: string;
  onToggleSeason?: () => void;
};

function formatWeekLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatMonth(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { month: "short" });
}

function groupByMonth(weeks: TimingWeek[]): Map<string, TimingWeek[]> {
  const map = new Map<string, TimingWeek[]>();
  for (const w of weeks) {
    const key = w.weekStart.slice(0, 7);
    const list = map.get(key) ?? [];
    list.push(w);
    map.set(key, list);
  }
  return map;
}

export function TimingHeatmap({
  weeks,
  seasonLabel,
  alternateSeasonLabel,
  onToggleSeason,
}: Props) {
  const [tooltip, setTooltip] = useState<TimingWeek | null>(null);
  const months = groupByMonth(weeks);

  const seasonAvgPrice = (() => {
    const priced = weeks.filter((w) => w.liftPassPrice);
    if (priced.length === 0) return null;
    return priced.reduce((sum, w) => sum + w.liftPassPrice!.priceEur, 0) / priced.length;
  })();

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-serif text-[18px] md:text-[22px] font-bold text-navy">
          Best time to visit
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold text-navy">{seasonLabel}</span>
          {alternateSeasonLabel && onToggleSeason && (
            <button
              onClick={onToggleSeason}
              className="text-[12px] text-gold font-semibold hover:text-gold-light transition-colors"
            >
              View {alternateSeasonLabel} →
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0">
        <div className="inline-flex gap-1 min-w-full md:min-w-0">
          {[...months.entries()].map(([monthKey, monthWeeks]) => (
            <div key={monthKey} className="flex-1 min-w-[60px]">
              <div className="text-[10px] font-semibold uppercase tracking-[1px] text-text-muted text-center mb-2">
                {formatMonth(monthWeeks[0].weekStart)}
              </div>
              <div className="flex flex-col gap-1">
                {monthWeeks.map((w) => {
                  const pct = Math.round(w.rawScore * 100);
                  const style = timingScoreStyle(pct);
                  return (
                    <button
                      key={w.weekStart}
                      className={`
                        relative w-full rounded-[6px] p-1.5 md:p-2 text-center transition-all
                        ${w.isPast ? "opacity-40" : "hover:-translate-y-0.5 hover:shadow-md cursor-pointer"}
                        ${style.bg} ${style.text}
                      `}
                      disabled={w.isPast}
                      onClick={() => setTooltip(tooltip?.weekStart === w.weekStart ? null : w)}
                      onMouseEnter={() => !w.isPast && setTooltip(w)}
                      onMouseLeave={() => setTooltip(null)}
                      aria-label={`Week of ${formatWeekLabel(w.weekStart)}: ${pct}%`}
                    >
                      <div className="font-serif text-[14px] md:text-[16px] font-bold leading-none">
                        {pct}
                      </div>
                      <div className="text-[8px] md:text-[9px] opacity-80 mt-0.5 leading-tight">
                        {formatWeekLabel(w.weekStart).split(" ")[0]}
                      </div>
                      {!w.isPast && w.liftPassPrice && seasonAvgPrice && (
                        <div className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full ${
                          w.liftPassPrice.priceEur < seasonAvgPrice * 0.95
                            ? "bg-emerald-300"
                            : w.liftPassPrice.priceEur > seasonAvgPrice * 1.05
                              ? "bg-red-300"
                              : "bg-amber-300"
                        }`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {tooltip && (() => {
        const pct = Math.round(tooltip.rawScore * 100);
        const style = timingScoreStyle(pct);
        return (
          <div className="mt-4 bg-white rounded-[12px] border border-border-light p-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <span className="font-serif text-[16px] font-semibold text-navy">
                Week of {formatWeekLabel(tooltip.weekStart)}
              </span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${style.bg} ${style.text}`}>
                {style.label}
              </span>
            </div>
            <div className={`grid gap-3 text-[12px] ${tooltip.liftPassPrice ? "grid-cols-4" : "grid-cols-3"}`}>
              <div>
                <div className="text-text-muted">Conditions</div>
                <div className="font-semibold text-navy">
                  {tooltip.conditionsScore !== null ? scoreOutOf100Label(tooltip.conditionsScore) : "No data"}
                </div>
              </div>
              <div>
                <div className="text-text-muted">Crowd pressure</div>
                <div className="font-semibold text-navy">{Math.round(tooltip.crowdPressure * 100)}%</div>
              </div>
              {tooltip.liftPassPrice && (
                <div>
                  <div className="text-text-muted">6-day pass</div>
                  <div className="font-semibold text-navy">
                    {tooltip.liftPassPrice.currency === "EUR"
                      ? `EUR ${tooltip.liftPassPrice.priceLocal}`
                      : `${tooltip.liftPassPrice.currency} ${tooltip.liftPassPrice.priceLocal} (EUR ${tooltip.liftPassPrice.priceEur})`}
                  </div>
                </div>
              )}
              <div>
                <div className="text-text-muted">Timing score</div>
                <div className="font-semibold text-navy">{pct}%</div>
              </div>
            </div>
            {tooltip.activeHolidays.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border-light">
                <div className="text-[11px] text-text-muted mb-1">Active holidays:</div>
                <div className="flex flex-wrap gap-1.5">
                  {tooltip.activeHolidays
                    .filter((h, i, a) => a.findIndex((x) => x.holidayName === h.holidayName && x.countryCode === h.countryCode) === i)
                    .map((h, i) => (
                      <span key={i} className="text-[10px] bg-warm-bg px-2 py-0.5 rounded text-text-body">
                        {h.countryCode}: {h.holidayName}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="flex flex-wrap gap-3 mt-4 justify-center">
        {TIMING_BANDS.map((band) => (
          <div key={band.label} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-[3px] ${band.bg}`} />
            <span className="text-[10px] text-text-muted">{band.minPct}%+: {band.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
